import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicMiniSiteSuccess() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | unpaid | expired | error
  const [info, setInfo] = useState({});
  const polls = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!sid) { setStatus("error"); return; }

    const tick = async () => {
      polls.current += 1;
      if (polls.current > 10) { setStatus("expired"); return; }
      try {
        const r = await axios.get(`${API}/public/checkout/status/${sid}`);
        setInfo(r.data);
        if (r.data.payment_status === "paid") setStatus("paid");
        else if (r.data.status === "expired") setStatus("expired");
        else timer.current = setTimeout(tick, 2000);
      } catch {
        timer.current = setTimeout(tick, 3000);
      }
    };
    tick();
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [sid]);

  const accent = "#002FA7";
  const card = (icon, color, title, body) => (
    <div className="bg-white border border-[#DEE2E6] max-w-lg w-full p-10 text-center">
      <div className="mx-auto mb-4 inline-flex">{icon}</div>
      <h1 className="font-display text-3xl font-black tracking-tight" style={{ color }}>{title}</h1>
      <p className="mt-3 text-sm text-[#495057]">{body}</p>
      {info?.amount_total != null && status === "paid" && (
        <div className="mt-6 inline-flex items-baseline gap-2 px-4 py-2 bg-[#F8F9FA] border border-[#DEE2E6]">
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#868E96]">Montant</span>
          <span className="font-display font-black text-2xl">{(info.amount_total / 100).toFixed(2)} {(info.currency || "EUR").toUpperCase()}</span>
        </div>
      )}
      <div className="mt-8 flex justify-center gap-2">
        <Link to={`/sites/${code}`}><Button variant="outline" data-testid="success-back" className="h-11">Retour au mini-site</Button></Link>
        <Link to="/"><Button className="h-11 bg-[#002FA7] text-white" data-testid="success-home">Accueil PAG</Button></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6" data-testid="minisite-success">
      {status === "polling" && card(
        <Loader2 className="h-14 w-14 animate-spin" style={{ color: accent }} />,
        accent,
        "Vérification du paiement…",
        "Veuillez patienter quelques secondes pendant que nous confirmons votre transaction."
      )}
      {status === "paid" && card(
        <CheckCircle2 className="h-14 w-14" style={{ color: "#059669" }} />,
        "#059669",
        "Paiement confirmé",
        "Merci ! Votre réservation est enregistrée. L'entreprise vous recontactera très prochainement."
      )}
      {status === "expired" && card(
        <Clock className="h-14 w-14" style={{ color: "#D97706" }} />,
        "#D97706",
        "Délai dépassé",
        "Nous n'avons pas reçu la confirmation à temps. Si le paiement a abouti côté Stripe, l'entreprise sera notifiée automatiquement."
      )}
      {status === "error" && card(
        <XCircle className="h-14 w-14" style={{ color: "#E60000" }} />,
        "#E60000",
        "Session introuvable",
        "Le lien de paiement est invalide ou expiré. Veuillez relancer une réservation."
      )}
    </div>
  );
}
